/**
 * HeaderBrand - Left section of the Header toolbar
 *
 * Renders the hamburger menu toggle alongside the OpenGov logo and active entity name.
 */

import { Box, ButtonBase, SvgIcon, Typography } from '@mui/material';
import {
  OpenGovLogoBlurple,
  OpenGovLogoIcon,
  Menu as MenuIcon,
} from '@opengov/react-capital-assets';
import { TestIds } from '../../constants';

interface HeaderBrandProps {
  onMenuClick: () => void;
  entityDisplayName: string;
}

export function HeaderBrand({ onMenuClick, entityDisplayName }: HeaderBrandProps) {
  return (
    <ButtonBase
      data-testid={TestIds.HAMBURGER_MENU}
      onClick={onMenuClick}
      aria-label="Open navigation menu"
      disableRipple
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderRadius: 1,
        px: 1,
        py: 0.5,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
        },
      }}
    >
      <SvgIcon component={MenuIcon} inheritViewBox sx={{ color: '#4b3fff' }} aria-hidden />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
        }}
      >
        <SvgIcon
          component={OpenGovLogoIcon}
          inheritViewBox
          sx={{ display: { xs: 'inline-flex', sm: 'none' }, fontSize: 24 }}
        />
        <SvgIcon
          component={OpenGovLogoBlurple}
          inheritViewBox
          sx={{ display: { xs: 'none', sm: 'inline-flex' }, width: 120, height: 'auto' }}
        />
        <Typography
          variant="caption"
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontSize: '0.75rem',
            lineHeight: 1,
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          {entityDisplayName}
        </Typography>
      </Box>
    </ButtonBase>
  );
}
